import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, MapPin, Star, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable Google Places autocomplete component.
 * Props:
 *  - onSelect(place) — fired when user selects a place (with details)
 *  - getToken — async fn that returns auth token
 *  - placeholder — input placeholder
 *  - initialValue — pre-fill input text
 */
export default function GooglePlacesSearch({ onSelect, getToken, placeholder, initialValue }) {
  const [query, setQuery] = useState(initialValue || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}/api/places/autocomplete?query=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setOpen(true);
      }
    } catch (err) {
      console.error('Places autocomplete error:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = async (suggestion) => {
    setOpen(false);
    setQuery(suggestion.structured_name?.main || suggestion.description);
    setDetailsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}/api/places/details/${suggestion.place_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const place = await res.json();
        setSelected(place);
        onSelect?.(place);
      }
    } catch (err) {
      console.error('Places details error:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setSelected(null);
    setSuggestions([]);
    onSelect?.(null);
  };

  const renderStars = (rating) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return (
      <span className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-3.5 h-3.5 ${
              i < full ? 'text-yellow-400 fill-yellow-400' :
              i === full && half ? 'text-yellow-400 fill-yellow-400/50' :
              'text-gray-300'
            }`}
          />
        ))}
      </span>
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          placeholder={placeholder || 'Search your business on Google...'}
          className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20 text-sm"
          data-testid="google-places-input"
        />
        {(query || loading) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3 border-b border-gray-50 last:border-0"
            >
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {s.structured_name?.main || s.description}
                </div>
                {s.structured_name?.secondary && (
                  <div className="text-xs text-gray-500 truncate">
                    {s.structured_name.secondary}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Selected place card */}
      {detailsLoading && (
        <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
          <span className="text-sm text-gray-600">Loading business details...</span>
        </div>
      )}

      {selected && !detailsLoading && (
        <div className="mt-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-gray-900 text-sm">{selected.name}</div>
              <div className="text-xs text-gray-600 mt-0.5">{selected.address}</div>
              {selected.rating > 0 && (
                <div className="flex items-center gap-2 mt-1.5">
                  {renderStars(selected.rating)}
                  <span className="text-xs text-gray-600">
                    {selected.rating} ({selected.review_count} reviews)
                  </span>
                </div>
              )}
            </div>
            <button type="button" onClick={handleClear} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
